import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  collection,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  where,
  getDocs,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import GrassIcon from "@mui/icons-material/Grass";
import HistoryIcon from "@mui/icons-material/History";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import moment from "moment";
import { nanoid } from "nanoid";

import { firebaseAuth } from "../../components/firebase/auth";
import { firebaseStore } from "../../components/firebase/firestore";
import { mergeBatchDateWindow, subscribeRecentBatchesByDate } from "./batchDateLoading";
import {
  FreezeDryBatchStatus,
  getFreezeDryBatchStatus,
  freezeDryBatchStatusLabel,
} from "./freezeDryBatchStatus";
import { shopStockApiConfigured, type ProcessingBatchDraft, type ProcessingBatchResult } from "./webshop/shopAdminApi";
import { useProcessingBatchWriter } from "./webshop/useProcessingBatchWriter";
import WebshopCatalogDeletion from "./webshop/WebshopCatalogDeletion";
import "./dashboard.css";
import "./batchWorkspace.css";

interface FreezeDryProduct {
  id: string;
  name_sv: string;
  name_en: string;
  icon: string;
  url: string;
}

type DryProcess = "freeze_dried" | "dehydrated";
type BatchSource = "grown_by_us" | "external";

interface FreezeDryBatch {
  firebaseId: string;
  batchId: string;
  product: string;
  productId: string;
  process?: DryProcess;
  sourceType?: BatchSource;
  externalProductName?: string | null;
  externalCountry?: string | null;
  externalPurchasedFrom?: string | null;
  externalPricePerKg?: number | null;
  sourceFreshBatchRef: string | null;
  sourceFreshBatchId: string | null;
  plantDate: number | null;
  plantTime: string | null;
  plantedBy: string | null;
  harvestDate: number | null;
  harvestTime: string | null;
  harvestBy: string | null;
  freezeDryStart: number | null;
  freezeDryEnd: number | null;
  freezeDryBy: string | null;
  extraInfo: string | null;
  freshWeight: number | null;
  dryWeight: number | null;
  trayCount: number | null;
  createdAt: number;
}

interface FreshProductBatch {
  firebaseId: string;
  productId: string;
  batchId: string;
  plantDate: number | null;
  plantedBy: string;
  harvestDate: number | null;
  harvestedBy: string;
  trayCount: number | null;
  freshWeight: number | null;
  notes: string;
  createdAt: number;
}

const PLANTED_BY_OPTIONS = [
  "Ann-Helen",
  "Adrian",
  "Jimmy",
  "Ann-Helen & Adrian",
  "Ann-Helen & Jimmy",
  "Adrian & Jimmy",
  "Ann-Helen, Adrian & Jimmy",
];

const PROCESS_OPTIONS: Array<{ value: DryProcess; label: string }> = [
  { value: "freeze_dried", label: "Frystorkad" },
  { value: "dehydrated", label: "Dehydrerad" },
];

const PROCESS_LABELS: Record<DryProcess, string> = {
  freeze_dried: "Frystorkade",
  dehydrated: "Dehydrerade",
};

const PROCESS_SUMMARIES: Record<DryProcess, string> = {
  freeze_dried: "Frystorkning i frystork",
  dehydrated: "Torkning i dehydratorugn",
};

const BATCH_SOURCE_OPTIONS: Array<{ value: BatchSource; label: string }> = [
  { value: "grown_by_us", label: "Odlad av oss" },
  { value: "external", label: "Externt inköpt" },
];

const DRY_PROCESSES: DryProcess[] = ["freeze_dried", "dehydrated"];

const FREEZE_DRY_STATUSES: FreezeDryBatchStatus[] = ["active", "future", "historic"];

const STATUS_DESCRIPTIONS: Record<FreezeDryBatchStatus, string> = {
  active: "Pågående och nyligen processade batcher",
  future: "Batcher med ett kommande startdatum",
  historic: "Batcher med slutdatum, eller enbart startdatum, äldre än en vecka",
};

const isExternalBatch = (batch: FreezeDryBatch) =>
  batch.sourceType === "external" || (!batch.sourceType && batch.productId === "external");

const formatWeight = (weight: number | null) =>
  weight == null ? "—" : `${weight.toLocaleString("sv-SE")} g`;

// Staged loading by process start. The initial query includes the last
// 2 weeks, future starts and undated records. Each stage fetches older starts.
// `months` is the upper age bound (in months) of the window being loaded.
const LOAD_STAGES: Array<{ key: string; label: string; months: number | null }> = [
  { key: "month", label: "senaste månaden", months: 1 },
  { key: "threeMonths", label: "senaste 3 månaderna", months: 3 },
  { key: "sixMonths", label: "senaste 6 månaderna", months: 6 },
  { key: "year", label: "senaste året", months: 12 },
  { key: "threeYears", label: "senaste 3 åren", months: 36 },
  { key: "all", label: "allt", months: null },
];

// Age (in ms) of the initial processing-date cutoff.
const INITIAL_CUTOFF_MS = 2 * 7 * 24 * 60 * 60 * 1000;

function DashBoardFreezeDry() {
  const [user, loading] = useAuthState(firebaseAuth);
  const navigate = useNavigate();

  // Dialog state
  const [open, setOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState<FreezeDryBatch | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const protectedWriter = shopStockApiConfigured();
  const writer = useProcessingBatchWriter(user);
  const formLocked = dialogLoading || writer.loading || writer.saving || writer.pending;
  const [deleteTarget, setDeleteTarget] = useState<FreezeDryBatch | null>(null);
  const submittedDraft = useRef<Partial<FreezeDryBatch> | null>(null);

  // Products from products_fresh collection
  const [products, setProducts] = useState<FreezeDryProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<FreezeDryProduct | null>(null);

  // Batches, stored per process group. Each group is lazily loaded from
  // Firestore the first time its dropdown is opened, and the listener stays
  // active afterwards.
  const [batchesByProcess, setBatchesByProcess] = useState<Record<DryProcess, FreezeDryBatch[]>>({
    freeze_dried: [],
    dehydrated: [],
  });
  const [sortBy, setSortBy] = useState<"recent" | "product" | "started" | "finished">("recent");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<FreezeDryBatchStatus | "all">("all");
  const [errorsByProcess, setErrorsByProcess] = useState<Partial<Record<DryProcess, string>>>({});
  const [saveError, setSaveError] = useState("");
  const loadAnchor = useRef(Date.now());

  // Fresh batches from products_fresh_batch
  const [freshBatches, setFreshBatches] = useState<FreshProductBatch[]>([]);
  const [selectedFreshBatch, setSelectedFreshBatch] = useState<FreshProductBatch | null>(null);

  // Form fields
  const [process, setProcess] = useState<DryProcess>("freeze_dried");
  const [sourceType, setSourceType] = useState<BatchSource>("grown_by_us");
  const [externalProductName, setExternalProductName] = useState("");
  const [externalCountry, setExternalCountry] = useState("");
  const [externalPurchasedFrom, setExternalPurchasedFrom] = useState("");
  const [externalPricePerKg, setExternalPricePerKg] = useState<number | null>(null);
  const [freezeDryStart, setFreezeDryStart] = useState<number | null>(null);
  const [freezeDryStartTime, setFreezeDryStartTime] = useState<moment.Moment | null>(null);
  const [freezeDryEnd, setFreezeDryEnd] = useState<number | null>(null);
  const [freezeDryEndTime, setFreezeDryEndTime] = useState<moment.Moment | null>(null);
  const [freezeDryBy, setFreezeDryBy] = useState("");
  const [extraInfo, setExtraInfo] = useState("");
  const [freshWeight, setFreshWeight] = useState<number | null>(null);
  const [dryWeight, setDryWeight] = useState<number | null>(null);
  const [trayCount, setTrayCount] = useState<number | null>(null);

  // --- Dropdown / lazy-loading state ---
  // Which process groups are currently expanded. Freeze-dried is open by default.
  const [expandedProcesses, setExpandedProcesses] = useState<Record<DryProcess, boolean>>({
    freeze_dried: true,
    dehydrated: false,
  });
  // Whether a group has been opened at least once (so its listener is active).
  const [loadedProcesses, setLoadedProcesses] = useState<Record<DryProcess, boolean>>({
    freeze_dried: true,
    dehydrated: true,
  });
  // Per-group loading flags for the initial fetch.
  const [loadingByProcess, setLoadingByProcess] = useState<Record<DryProcess, boolean>>({
    freeze_dried: true,
    dehydrated: true,
  });
  // Which load stage has been reached for each process group.
  const [loadStageByProcess, setLoadStageByProcess] = useState<Record<DryProcess, number>>({
    freeze_dried: 0,
    dehydrated: 0,
  });
  const [loadingMoreByProcess, setLoadingMoreByProcess] = useState<Record<DryProcess, boolean>>({
    freeze_dried: false,
    dehydrated: false,
  });
  // Holds the active unsubscribe functions per group so listeners persist.
  const unsubscribeRefs = useRef<Partial<Record<DryProcess, () => void>>>({});

  // Listen to products from products_fresh
  useEffect(() => {
    const q = query(
      collection(firebaseStore, "products_fresh"),
      orderBy("name_sv")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const temp: FreezeDryProduct[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        temp.push({ id: doc.id, name_sv: data.name_sv || "", name_en: data.name_en || "", icon: data.icon || "", url: data.url || "" });
      });
      setProducts(temp);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user === null && loading === false) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  // Listen to fresh batches from products_fresh_batch
  useEffect(() => {
    const q = query(
      collection(firebaseStore, "products_fresh_batch"),
      orderBy("plantDate", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const temp: FreshProductBatch[] = [];
      snapshot.forEach((doc) => {
        temp.push({ firebaseId: doc.id, ...doc.data() } as FreshProductBatch);
      });
      setFreshBatches(temp);
    });
    return () => unsubscribe();
  }, []);

  // Load by process start, regardless of when a batch was entered in Firestore.
  const startGroupListener = useCallback((processType: DryProcess) => {
    if (unsubscribeRefs.current[processType]) return; // already listening
    const twoWeeksAgo = loadAnchor.current - INITIAL_CUTOFF_MS;
    setErrorsByProcess((prev) => ({ ...prev, [processType]: "" }));
    setLoadingByProcess((prev) => ({ ...prev, [processType]: true }));
    const unsubscribe = subscribeRecentBatchesByDate<FreezeDryBatch>(
      collection(firebaseStore, "freezedry_batches"), "freezeDryStart", twoWeeksAgo,
      (live) => {
        // Keep explicitly loaded history when the recent live query updates.
        setBatchesByProcess((prev) => ({
          ...prev,
          [processType]: mergeBatchDateWindow(prev[processType], live, "freezeDryStart", twoWeeksAgo,
            (batch) => (batch.process || "freeze_dried") === processType),
        }));
        setLoadingByProcess((prev) => ({ ...prev, [processType]: false }));
      },
      (error) => {
        console.error("Error loading batches:", error);
        setErrorsByProcess((prev) => ({ ...prev, [processType]: "Det gick inte att hämta batcherna." }));
        setLoadingByProcess((prev) => ({ ...prev, [processType]: false }));
        delete unsubscribeRefs.current[processType];
      },
    );
    unsubscribeRefs.current[processType] = unsubscribe;
  }, []);

  // Toggle a group's dropdown. Opening a group for the first time starts its
  // listener; closing it keeps the listener active so it isn't re-fetched.
  const handleToggleGroup = useCallback((processType: DryProcess) => {
    if (!expandedProcesses[processType]) {
      startGroupListener(processType);
      setLoadedProcesses((prev) => ({ ...prev, [processType]: true }));
    }
    setExpandedProcesses((prev) => ({ ...prev, [processType]: !prev[processType] }));
  }, [expandedProcesses, startGroupListener]);

  // Clean up all listeners on unmount.
  useEffect(() => {
    return () => {
      Object.values(unsubscribeRefs.current).forEach((unsub) => unsub && unsub());
      unsubscribeRefs.current = {};
    };
  }, []);

  // Both process groups load their docs when the tab is opened. Freeze-dried
  // is expanded by default; dehydrated stays collapsed but its listener is
  // started on mount so its list is loaded and stays up to date.
  useEffect(() => {
    DRY_PROCESSES.forEach((processType) => startGroupListener(processType));
  }, [startGroupListener]);

  // Load more (older) batches for a given process group, advancing one stage.
  const handleLoadMore = async (processType: DryProcess) => {
    const currentStage = loadStageByProcess[processType];
    if (currentStage >= LOAD_STAGES.length) return; // all loaded
    const stage = LOAD_STAGES[currentStage];
    setLoadingMoreByProcess((prev) => ({ ...prev, [processType]: true }));
    setErrorsByProcess((prev) => ({ ...prev, [processType]: "" }));
    try {
      const now = moment(loadAnchor.current);
      // Upper bound of this window: the previous stage's cutoff.
      const upperBound = currentStage === 0
        ? now.clone().subtract(INITIAL_CUTOFF_MS, "ms").valueOf()
        : now.clone().subtract(LOAD_STAGES[currentStage - 1].months!, "months").valueOf();
      // Lower bound: if this stage has a month limit, load docs at least that
      // old; otherwise (all) there is no lower bound.
      const lowerBound = stage.months !== null
        ? now.clone().subtract(stage.months, "months").valueOf()
        : 0;

      const q = query(
        collection(firebaseStore, "freezedry_batches"),
        where("freezeDryStart", ">=", lowerBound),
        where("freezeDryStart", "<", upperBound),
        orderBy("freezeDryStart", "desc")
      );
      const snapshot = await getDocs(q);
      const older: FreezeDryBatch[] = [];
      snapshot.forEach((doc) => {
        const batch = { firebaseId: doc.id, ...doc.data() } as FreezeDryBatch;
        if ((batch.process || "freeze_dried") === processType) older.push(batch);
      });
      setBatchesByProcess((prev) => {
        const existing = new Set(prev[processType].map((b) => b.firebaseId));
        const merged = [...prev[processType], ...older.filter((b) => !existing.has(b.firebaseId))];
        return { ...prev, [processType]: merged };
      });
      setLoadStageByProcess((prev) => ({ ...prev, [processType]: currentStage + 1 }));
    } catch (error) {
      console.error("Error loading more batches:", error);
      setErrorsByProcess((prev) => ({ ...prev, [processType]: "Det gick inte att hämta äldre batcher. Försök igen." }));
    } finally {
      setLoadingMoreByProcess((prev) => ({ ...prev, [processType]: false }));
    }
  };

  const getNextLoadStage = (processType: DryProcess): { label: string; isLast: boolean } | null => {
    const currentStage = loadStageByProcess[processType];
    if (currentStage >= LOAD_STAGES.length) return null;
    const stage = LOAD_STAGES[currentStage];
    return { label: stage.label, isLast: stage.months === null };
  };

  const resetForm = () => {
    writer.reset(); submittedDraft.current = null;
    setSaveError("");
    setSelectedProduct(null);
    setSelectedFreshBatch(null);
    setProcess("freeze_dried");
    setSourceType("grown_by_us");
    setExternalProductName("");
    setExternalCountry("");
    setExternalPurchasedFrom("");
    setExternalPricePerKg(null);
    setFreezeDryStart(null);
    setFreezeDryStartTime(null);
    setFreezeDryEnd(null);
    setFreezeDryEndTime(null);
    setFreezeDryBy("");
    setExtraInfo("");
    setFreshWeight(null);
    setDryWeight(null);
    setTrayCount(null);
    setEditingBatch(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setOpen(true);
  };

  const populateForm = (batch: FreezeDryBatch) => {
    setEditingBatch(batch);
    const product = products.find((p) => p.id === batch.productId);
    setSelectedProduct(product || (protectedWriter && batch.productId !== "external" ? { id: batch.productId, name_sv: batch.product, name_en: "", icon: "", url: "" } : null));
    // Find and set the linked fresh batch
    const freshBatch = batch.sourceFreshBatchRef
      ? freshBatches.find((fb) => fb.firebaseId === batch.sourceFreshBatchRef)
      : undefined;
    setSelectedFreshBatch(freshBatch || (protectedWriter && batch.sourceFreshBatchRef ? { firebaseId: batch.sourceFreshBatchRef, batchId: batch.sourceFreshBatchId || batch.sourceFreshBatchRef, productId: batch.productId, plantDate: null, plantedBy: "", harvestDate: null, harvestedBy: "", trayCount: null, freshWeight: null, notes: "", createdAt: 0 } : null));
    setProcess(batch.process || "freeze_dried");
    // Existing batches were created before source tracking existed and are
    // treated as grown by us unless explicitly marked as external. Older
    // external records may only have their name in `product`, so use it as a
    // fallback to keep the edit dialog saveable.
    const batchSource = batch.sourceType || (batch.productId === "external" ? "external" : "grown_by_us");
    setSourceType(batchSource);
    setExternalProductName(batch.externalProductName || (batchSource === "external" ? batch.product : ""));
    setExternalCountry(batch.externalCountry || "");
    setExternalPurchasedFrom(batch.externalPurchasedFrom || "");
    setExternalPricePerKg(batch.externalPricePerKg ?? null);
    setFreezeDryStart(batch.freezeDryStart);
    setFreezeDryStartTime(batch.freezeDryStart ? moment(batch.freezeDryStart) : null);
    setFreezeDryEnd(batch.freezeDryEnd);
    setFreezeDryEndTime(batch.freezeDryEnd ? moment(batch.freezeDryEnd) : null);
    setFreezeDryBy(batch.freezeDryBy || "");
    setExtraInfo(batch.extraInfo || "");
    setFreshWeight(batch.freshWeight);
    setDryWeight(batch.dryWeight);
    setTrayCount(batch.trayCount);
    setOpen(true);
  };

  const loadCurrent = (batch: FreezeDryBatch) => writer.load(batch.firebaseId, (snapshot) =>
    populateForm({ ...batch, ...snapshot.draft, batchId: snapshot.businessId }));

  const handleOpenEdit = (batch: FreezeDryBatch) => {
    if (formLocked) return;
    resetForm(); populateForm(batch);
    if (protectedWriter) void loadCurrent(batch);
  };

  const handleClose = () => {
    if (formLocked) return;
    setOpen(false);
    resetForm();
  };

  const handleDeleteBatch = async (batch: FreezeDryBatch) => {
    if (formLocked) return;
    if (protectedWriter) { setDeleteTarget(batch); return; }
    if (window.confirm(`Ta bort batch ${batch.batchId} (${batch.product})?`)) {
      try {
        await deleteDoc(doc(firebaseStore, "freezedry_batches", batch.firebaseId));
        setBatchesByProcess((prev) => ({
          freeze_dried: prev.freeze_dried.filter((item) => item.firebaseId !== batch.firebaseId),
          dehydrated: prev.dehydrated.filter((item) => item.firebaseId !== batch.firebaseId),
        }));
      } catch (error) {
        console.error("Error deleting batch:", error);
        setErrorsByProcess((prev) => ({ ...prev, [batch.process || "freeze_dried"]: "Batchen kunde inte tas bort. Försök igen." }));
      }
    }
  };

  const generateBatchId = (productName: string, startTimestamp: number): string => {
    const sanitized = productName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^A-Za-z0-9]/g, "");
    const prefix = sanitized.slice(0, 3).toUpperCase();
    const dateStr = moment(startTimestamp).format("YYMMDD");
    const shortId = nanoid(4).toUpperCase();
    return `${prefix}-${dateStr}-${shortId}`;
  };

  // Fresh batch helpers
  const getFreshBatchesForProduct = (productId: string): FreshProductBatch[] => {
    return freshBatches.filter((fb) => fb.productId === productId);
  };

  const parentProductName = (productId: string): string => {
    if (productId === "external") return "Externt inköpt";
    const product = products.find((p) => p.id === productId);
    if (product) {
      return `${product.name_sv}${product.name_en ? ` / ${product.name_en}` : ""}`;
    }
    const batch = batchesByProcess.freeze_dried.find((b) => b.productId === productId)
      || batchesByProcess.dehydrated.find((b) => b.productId === productId);
    return batch ? batch.product : productId;
  };

  const getSortedBatches = (processType: DryProcess): FreezeDryBatch[] => {
    const sorted = [...batchesByProcess[processType]];
    if (sortBy === "product") {
      sorted.sort((a, b) => (a.product || "").localeCompare(b.product || "", "sv-SE"));
    } else if (sortBy === "started") {
      sorted.sort((a, b) => (a.freezeDryStart || 0) - (b.freezeDryStart || 0));
    } else if (sortBy === "finished") {
      sorted.sort((a, b) => (b.freezeDryEnd || b.freezeDryStart || 0) - (a.freezeDryEnd || a.freezeDryStart || 0));
    } else {
      sorted.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    }
    return sorted;
  };

  const searchTerm = search.trim().toLocaleLowerCase("sv-SE");
  const matchesSearch = (batch: FreezeDryBatch) => [
    batch.batchId, batch.product, parentProductName(batch.productId),
    batch.sourceFreshBatchId, batch.externalProductName, batch.externalCountry,
    batch.externalPurchasedFrom, batch.freezeDryBy, batch.extraInfo,
  ].some((value) => value?.toLocaleLowerCase("sv-SE").includes(searchTerm));

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
  };

  const handleFreshBatchChange = (freshBatch: FreshProductBatch | null) => {
    setSelectedFreshBatch(freshBatch);
    if (freshBatch) {
      setTrayCount(freshBatch.trayCount);
      setFreshWeight(freshBatch.freshWeight);
    } else {
      setTrayCount(null);
      setFreshWeight(null);
    }
  };

  const completeProtectedSave = (result: ProcessingBatchResult) => {
    if (editingBatch && submittedDraft.current) {
      const updated = { ...editingBatch, ...submittedDraft.current, batchId: result.businessId };
      setBatchesByProcess((prev) => ({
        freeze_dried: [...prev.freeze_dried.filter((batch) => batch.firebaseId !== editingBatch.firebaseId), ...(process === "freeze_dried" ? [updated] : [])],
        dehydrated: [...prev.dehydrated.filter((batch) => batch.firebaseId !== editingBatch.firebaseId), ...(process === "dehydrated" ? [updated] : [])],
      }));
    }
    clearFilters(); setExpandedProcesses((prev) => ({ ...prev, [process]: true })); startGroupListener(process);
    setOpen(false); resetForm();
  };

  const handleSave = async () => {
    if (protectedWriter && writer.pending) {
      const result = await writer.retry();
      if (result) completeProtectedSave(result);
      return;
    }
    if (sourceType === "grown_by_us" && !selectedProduct) {
      setSaveError("Välj en produkt från listan.");
      return;
    }
    if (sourceType === "external" && !externalProductName.trim()) {
      setSaveError("Ange produktnamn för den externt inköpta produkten.");
      return;
    }
    if (!freezeDryStart && !editingBatch?.freezeDryStart) {
      setSaveError("Ange ett startdatum för batchen.");
      return;
    }

    setSaveError("");
    if (!protectedWriter) setDialogLoading(true);

    const startTimestamp = freezeDryStart || editingBatch?.freezeDryStart || Date.now();
    const startTimeMinutes = freezeDryStartTime
      ? freezeDryStartTime.hours() * 60 + freezeDryStartTime.minutes()
      : 0;
    let startDateTime = moment(startTimestamp)
      .startOf("day")
      .add(startTimeMinutes, "minutes")
      .valueOf();

    const endTimestamp = freezeDryEnd;
    const endTimeMinutes = freezeDryEndTime
      ? freezeDryEndTime.hours() * 60 + freezeDryEndTime.minutes()
      : 0;
    let endDateTime = endTimestamp
      ? moment(endTimestamp).startOf("day").add(endTimeMinutes, "minutes").valueOf()
      : null;

    // Preserve seconds/milliseconds for unchanged historical picker values.
    if (editingBatch?.freezeDryStart && moment(startDateTime).isSame(editingBatch.freezeDryStart, "minute")) startDateTime = editingBatch.freezeDryStart;
    if (editingBatch?.freezeDryEnd && endDateTime && moment(endDateTime).isSame(editingBatch.freezeDryEnd, "minute")) endDateTime = editingBatch.freezeDryEnd;

    const batchData = {
      batchId: editingBatch
        ? editingBatch.batchId
        : protectedWriter ? "" : generateBatchId(sourceType === "external" ? externalProductName.trim() : selectedProduct!.name_sv, startDateTime),
      product: sourceType === "external" ? externalProductName.trim() : selectedProduct!.name_sv,
      productId: sourceType === "external" ? "external" : selectedProduct!.id,
      process,
      sourceType,
      externalProductName: sourceType === "external" ? externalProductName.trim() : null,
      externalCountry: sourceType === "external" ? externalCountry.trim() : null,
      externalPurchasedFrom: sourceType === "external" ? externalPurchasedFrom.trim() : null,
      externalPricePerKg: sourceType === "external" ? externalPricePerKg : null,
      sourceFreshBatchRef: sourceType === "grown_by_us" && selectedFreshBatch ? selectedFreshBatch.firebaseId : null,
      sourceFreshBatchId: sourceType === "grown_by_us" && selectedFreshBatch ? selectedFreshBatch.batchId : null,
      freezeDryStart: startDateTime,
      freezeDryEnd: endDateTime,
      freezeDryBy: freezeDryBy || null,
      extraInfo: extraInfo || null,
      freshWeight: freshWeight ?? null,
      dryWeight: dryWeight ?? null,
      trayCount: trayCount ?? null,
    };

    if (protectedWriter) {
      const { batchId: _batchId, product: _product, sourceFreshBatchId: _sourceFreshBatchId, ...draft } = batchData;
      submittedDraft.current = batchData;
      const result = await writer.save(editingBatch?.firebaseId, draft as ProcessingBatchDraft);
      if (result) completeProtectedSave(result);
      return;
    }

    try {
      if (editingBatch) {
        await updateDoc(
          doc(firebaseStore, "freezedry_batches", editingBatch.firebaseId),
          batchData
        );
        // Older batches are outside the live query, so reflect saved edits locally.
        setBatchesByProcess((prev) => {
          const updated = { ...editingBatch, ...batchData };
          return {
            freeze_dried: [
              ...prev.freeze_dried.filter((batch) => batch.firebaseId !== editingBatch.firebaseId),
              ...(process === "freeze_dried" ? [updated] : []),
            ],
            dehydrated: [
              ...prev.dehydrated.filter((batch) => batch.firebaseId !== editingBatch.firebaseId),
              ...(process === "dehydrated" ? [updated] : []),
            ],
          };
        });
      } else {
        await addDoc(collection(firebaseStore, "freezedry_batches"), {
          ...batchData,
          createdAt: Date.now(),
        });
      }
      if (!editingBatch || (editingBatch.process || "freeze_dried") !== process) {
        clearFilters();
        setExpandedProcesses((prev) => ({ ...prev, [process]: true }));
        setLoadedProcesses((prev) => ({ ...prev, [process]: true }));
        startGroupListener(process);
      }
      handleClose();
    } catch (error) {
      console.error("Error saving batch:", error);
      setSaveError("Batchen kunde inte sparas. Dina uppgifter finns kvar, försök igen.");
    }
    setDialogLoading(false);
  };

  const loadedBatches = [...batchesByProcess.freeze_dried, ...batchesByProcess.dehydrated];
  const searchedBatches = loadedBatches.filter(matchesSearch);
  const hasFilters = !!searchTerm || statusFilter !== "all";
  const filters = ["all", ...FREEZE_DRY_STATUSES] as const;

  return (
    <div className="DashBoardTabInner FreshDashboardTab BatchWorkspace">
      <div className="FreshTabToolbar">
        <div>
          <p className="FreshEyebrow">Produktion</p>
          <h2 className="FreshTabTitle">Processade batcher</h2>
          <p className="FreshTabSummary">Följ dina batcher från råvara till färdig produkt.</p>
        </div>
        <Button color="inherit" variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} disabled={formLocked || !!deleteTarget}>
          Ny batch
        </Button>
      </div>

      <div className="BatchTools" role="search" aria-label="Filtrera processade batcher">
        <div className="BatchSearchRow">
          <TextField
            className="BatchSearch"
            label="Sök batcher"
            placeholder="Produkt, batch-ID, leverantör…"
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            inputProps={{ "aria-describedby": "processed-batch-scope" }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Rensa sökning" onClick={() => setSearch("")}><CloseIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <TextField
            select label="Sortera" size="small" className="BatchSort"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value as typeof sortBy)}
          >
            <MenuItem value="recent">Senast skapad</MenuItem>
            <MenuItem value="product">Produkt A–Ö</MenuItem>
            <MenuItem value="started">Startdatum, äldst först</MenuItem>
            <MenuItem value="finished">Slutdatum, senast först</MenuItem>
          </TextField>
        </div>
        <div className="BatchFilterRow">
          <div className="BatchFilters" role="group" aria-label="Filtrera efter batchstatus">
            {filters.map((status) => {
              const count = status === "all" ? searchedBatches.length : searchedBatches.filter(
                (batch) => getFreezeDryBatchStatus(batch.freezeDryStart, batch.freezeDryEnd) === status
              ).length;
              return (
                <button
                  type="button" key={status}
                  className={`BatchFilter BatchFilter-${status}`}
                  aria-pressed={statusFilter === status}
                  title={status === "all" ? "Alla inlästa batcher som matchar sökningen" : STATUS_DESCRIPTIONS[status]}
                  onClick={() => setStatusFilter(status)}
                >
                  {status !== "all" && <span className={`BatchStatusDot BatchStatusDot-${status}`} />}
                  {status === "all" ? "Alla" : freezeDryBatchStatusLabel[status]}
                  <span className="BatchFilterCount">{count}</span>
                </button>
              );
            })}
          </div>
          {hasFilters && <Button color="inherit" size="small" onClick={clearFilters}>Rensa filter</Button>}
          <nav className="BatchJumpLinks" aria-label="Gå till process">
            {DRY_PROCESSES.map((processType) => (
              <a key={processType} href={`#processed-process-${processType}`} onClick={(event) => {
                event.preventDefault();
                setExpandedProcesses((prev) => ({ ...prev, [processType]: true }));
                setLoadedProcesses((prev) => ({ ...prev, [processType]: true }));
                startGroupListener(processType);
                requestAnimationFrame(() => {
                  document.getElementById(`processed-batches-heading-${processType}`)?.focus({ preventScroll: true });
                  document.getElementById(`processed-process-${processType}`)?.scrollIntoView({ block: "start" });
                });
              }}>
                {processType === "freeze_dried" ? <AcUnitIcon fontSize="small" /> : <WbSunnyOutlinedIcon fontSize="small" />}
                {PROCESS_LABELS[processType]}
              </a>
            ))}
          </nav>
        </div>
        <p className="BatchScope" id="processed-batch-scope">
          Sökning och filter gäller inlästa batcher. Öppna en process eller ladda äldre batcher för att visa fler.
        </p>
      </div>

      <div className="BatchProcesses">
        {DRY_PROCESSES.map((processType) => {
          const isExpanded = expandedProcesses[processType];
          const isLoaded = loadedProcesses[processType];
          const isLoading = loadingByProcess[processType];
          const processBatches = getSortedBatches(processType);
          const visibleBatches = processBatches.filter((batch) => matchesSearch(batch) && (
            statusFilter === "all" || getFreezeDryBatchStatus(batch.freezeDryStart, batch.freezeDryEnd) === statusFilter
          ));
          const next = getNextLoadStage(processType);
          const stage = loadStageByProcess[processType];
          const loadedPeriod = stage === 0 ? "senaste 2 veckorna" : LOAD_STAGES[stage - 1].label;
          const error = errorsByProcess[processType];
          return (
            <section className={`BatchProcess BatchProcess-${processType}`} id={`processed-process-${processType}`} key={processType}>
              <h3 className="BatchProcessHeading">
                <button
                  type="button"
                  className="BatchProcessToggle"
                  onClick={() => handleToggleGroup(processType)}
                  aria-expanded={isExpanded}
                  aria-controls={`processed-batches-${processType}`}
                  id={`processed-batches-heading-${processType}`}
                >
                  <span className="BatchProcessIcon">
                    {processType === "freeze_dried" ? <AcUnitIcon /> : <WbSunnyOutlinedIcon />}
                  </span>
                  <span className="BatchProcessTitle">
                    <strong>{PROCESS_LABELS[processType]}</strong>
                    <span>{PROCESS_SUMMARIES[processType]}</span>
                  </span>
                  <span className="BatchProcessCount">
                    {!isLoaded ? "Visa batcher" : isLoading ? "Laddar…" : `${hasFilters ? `${visibleBatches.length} av ` : ""}${processBatches.length} ${processBatches.length === 1 ? "batch" : "batcher"}`}
                  </span>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
              </h3>

              <div id={`processed-batches-${processType}`} hidden={!isExpanded} aria-labelledby={`processed-batches-heading-${processType}`}>
                {isExpanded && <>
                  {error && <Alert severity="error" className="BatchError" action={
                    !unsubscribeRefs.current[processType] ? <Button color="inherit" size="small" onClick={() => startGroupListener(processType)}>Försök igen</Button> : undefined
                  }>{error}</Alert>}
                  {isLoading ? (
                    <div className="BatchEmpty" role="status"><CircularProgress color="inherit" size={24} /><p>Hämtar batcher…</p></div>
                  ) : visibleBatches.length === 0 ? (
                    <div className="BatchEmpty" role="status">
                      <SearchIcon />
                      <strong>{hasFilters ? "Inga batcher matchar dina filter" : "Inga batcher i den här perioden"}</strong>
                      <p>{hasFilters ? "Prova en annan sökning, rensa filtren eller ladda äldre batcher." : "Skapa en ny batch eller ladda äldre batcher nedan."}</p>
                      {hasFilters && <Button color="inherit" size="small" onClick={clearFilters}>Rensa filter</Button>}
                    </div>
                  ) : (
                    <div className="BatchStatusGroups">
                      {FREEZE_DRY_STATUSES.map((status) => {
                        const statusBatches = visibleBatches.filter((batch) => getFreezeDryBatchStatus(batch.freezeDryStart, batch.freezeDryEnd) === status);
                        if (!statusBatches.length) return null;
                        return (
                          <section className="BatchStatusGroup" key={status}>
                            <div className="BatchStatusHeading">
                              <h4 title={STATUS_DESCRIPTIONS[status]}><span className={`BatchStatusDot BatchStatusDot-${status}`} />{freezeDryBatchStatusLabel[status]}<span>{statusBatches.length}</span></h4>
                              <p>{STATUS_DESCRIPTIONS[status]}</p>
                            </div>
                            <div className="BatchGrid">
                              {statusBatches.map((batch) => {
                                const product = products.find((item) => item.id === batch.productId);
                                const external = isExternalBatch(batch);
                                const productName = external ? batch.externalProductName || batch.product : product?.name_sv || batch.product;
                                const productImage = product?.icon || product?.url;
                                const hasDetails = batch.sourceFreshBatchId || batch.extraInfo || (external && (batch.externalCountry || batch.externalPurchasedFrom || batch.externalPricePerKg != null));
                                return (
                                  <article key={batch.firebaseId} className="BatchCard" aria-label={`${productName}, ${batch.batchId}`}>
                                    <div className="BatchCardHeader">
                                      <div className="BatchProductImage">
                                        {productImage ? <img src={productImage} alt="" loading="lazy" /> : <GrassIcon />}
                                      </div>
                                      <div className="BatchIdentity">
                                        <h5>{productName}</h5>
                                        <span className="BatchId">{batch.batchId}</span>
                                      </div>
                                      <IconButton color="inherit" title="Redigera batch" aria-label={`Redigera batch ${batch.batchId}`} disabled={formLocked} onClick={() => handleOpenEdit(batch)}><EditIcon fontSize="small" /></IconButton>
                                    </div>
                                    <div className="BatchCardMeta">
                                      <span className={`BatchSource BatchSource-${external ? "external" : "grown"}`}>{external ? "Externt inköpt" : "Odlad av oss"}</span>
                                      {batch.freezeDryBy && <span className="BatchOperator">{batch.freezeDryBy}</span>}
                                    </div>
                                    <dl className="BatchDates">
                                      <div><dt>Start</dt><dd>{batch.freezeDryStart ? <time dateTime={moment(batch.freezeDryStart).toISOString()}>{moment(batch.freezeDryStart).format("YYYY-MM-DD")}<span>{moment(batch.freezeDryStart).format("HH:mm")}</span></time> : "Ej angiven"}</dd></div>
                                      <div><dt>Slut</dt><dd>{batch.freezeDryEnd ? <time dateTime={moment(batch.freezeDryEnd).toISOString()}>{moment(batch.freezeDryEnd).format("YYYY-MM-DD")}<span>{moment(batch.freezeDryEnd).format("HH:mm")}</span></time> : "Ej angivet"}</dd></div>
                                    </dl>
                                    <dl className="BatchWeights">
                                      <div><dt>Färskvikt</dt><dd>{formatWeight(batch.freshWeight)}</dd></div>
                                      <div><dt>Torrvikt</dt><dd>{formatWeight(batch.dryWeight)}</dd></div>
                                      {!external && <div><dt>Brickor</dt><dd>{batch.trayCount ?? "—"}</dd></div>}
                                    </dl>
                                    <div className="BatchCardFooter">
                                      {hasDetails ? (
                                        <details className="BatchDetails">
                                          <summary>Batchdetaljer</summary>
                                          <dl>
                                            {batch.sourceFreshBatchId && <div><dt>Färsk batch</dt><dd>{batch.sourceFreshBatchId}</dd></div>}
                                            {external && batch.externalCountry && <div><dt>Ursprungsland</dt><dd>{batch.externalCountry}</dd></div>}
                                            {external && batch.externalPurchasedFrom && <div><dt>Inköpt från</dt><dd>{batch.externalPurchasedFrom}</dd></div>}
                                            {external && batch.externalPricePerKg != null && <div><dt>Inköpspris</dt><dd>{batch.externalPricePerKg.toLocaleString("sv-SE")} kr/kg</dd></div>}
                                            {batch.extraInfo && <div><dt>Anteckningar</dt><dd>{batch.extraInfo}</dd></div>}
                                          </dl>
                                        </details>
                                      ) : <span className="BatchNoDetails">Inga extra uppgifter</span>}
                                      <IconButton className="BatchDelete" color="inherit" title="Ta bort batch" aria-label={`Ta bort batch ${batch.batchId}`} disabled={formLocked} onClick={() => handleDeleteBatch(batch)}><DeleteIcon fontSize="small" /></IconButton>
                                    </div>
                                  </article>
                                );
                              })}
                            </div>
                          </section>
                        );
                      })}
                    </div>
                  )}
                  {!isLoading && <div className="BatchLoadMore">
                    <p><HistoryIcon fontSize="small" /><span>{next ? `Inläst: ${loadedPeriod}` : "All historik är inläst"}<small>{visibleBatches.length} av {processBatches.length} inlästa batcher visas</small></span></p>
                    {next && <Button color="inherit" variant="outlined" size="small" onClick={() => handleLoadMore(processType)} disabled={loadingMoreByProcess[processType]} startIcon={loadingMoreByProcess[processType] ? <CircularProgress size={14} color="inherit" /> : <HistoryIcon />}>
                      {loadingMoreByProcess[processType] ? "Laddar äldre batcher…" : `Ladda ${next.label === "allt" ? "all historik" : next.label}`}
                    </Button>}
                  </div>}
                </>}
              </div>
            </section>
          );
        })}
      </div>

      <Dialog className="FreshDialog BatchDialog" maxWidth="md" open={open} onClose={formLocked ? undefined : handleClose} aria-labelledby="processed-batch-dialog-title">
        <DialogTitle id="processed-batch-dialog-title">
          <span>{editingBatch ? "Redigera batch" : "Ny processad batch"}
            <span className="BatchDialogSubtitle">{editingBatch ? editingBatch.batchId : "Fyll i råvara och startdatum. Komplettera med resultatet när batchen är klar."}</span>
          </span>
          <IconButton aria-label="Stäng" onClick={handleClose} disabled={formLocked}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {protectedWriter && <>
            <Alert severity="info">Använda batchers råvara, process, datum och spårbarhet behålls. Anteckningar och inköpspris kan kompletteras. Skapa en ny batch för andra ändringar.</Alert>
            {writer.loading && <p role="status">Hämtar aktuella batchuppgifter…</p>}
            {writer.error && <Alert severity="error">{writer.error}</Alert>}
            <TextField label="Orsak till ändringen" value={writer.reason} onChange={(event) => writer.setReason(event.target.value)}
              required fullWidth margin="normal" inputProps={{ maxLength: 500 }} disabled={formLocked || writer.conflict}
              helperText="Sparas i privat revisionshistorik. Högst 500 tecken." />
            {editingBatch ? <Button disabled={formLocked} onClick={() => void loadCurrent(editingBatch)}>Hämta aktuella uppgifter</Button> :
              writer.conflict && <Button disabled={formLocked} onClick={() => writer.reset()}>Granska nytt försök</Button>}
          </>}
          <div className="AddPlantDialog">
            <fieldset className="BatchFormSection">
              <legend>Process och råvara</legend>
            {/* Process selection */}
            <div style={{ width: "100%", marginBottom: 16 }}>
              <TextField
                select
                label="Process *"
                size="small"
                fullWidth
                value={process}
                onChange={(e) => setProcess(e.target.value as DryProcess)}
                disabled={formLocked}
              >
                {PROCESS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </div>

            <div style={{ width: "100%", marginBottom: 16 }}>
              <TextField
                select
                label="Råvarukälla *"
                size="small"
                fullWidth
                value={sourceType}
                onChange={(e) => {
                  const nextSource = e.target.value as BatchSource;
                  setSourceType(nextSource);
                  if (nextSource === "external") setSelectedFreshBatch(null);
                }}
                disabled={formLocked}
              >
                {BATCH_SOURCE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </div>

            {sourceType === "grown_by_us" ? (
              <>
                {/* Product selection - pick from products_fresh */}
            <div style={{ width: "100%", marginBottom: 16 }}>
              <Autocomplete
                options={products}
                getOptionLabel={(option) => option.name_sv}
                value={selectedProduct}
                onChange={(_event, newValue) => {
                  setSelectedProduct(newValue);
                  handleFreshBatchChange(null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Produkt *"
                    size="small"
                    fullWidth
                    disabled={formLocked}
                  />
                )}
                disabled={formLocked}
                fullWidth
              />
            </div>

            {/* Fresh batch selection - from products_fresh_batch for selected product */}
            <div style={{ width: "100%", marginBottom: 16 }}>
              <Autocomplete
                options={selectedProduct ? getFreshBatchesForProduct(selectedProduct.id) : []}
                getOptionLabel={(option) => `${option.batchId}${option.plantDate ? ` (sådd: ${moment(option.plantDate).format("YYYY-MM-DD")})` : ""}${option.harvestDate ? ` (skörd: ${moment(option.harvestDate).format("YYYY-MM-DD")})` : ""}`}
                value={selectedFreshBatch}
                onChange={(_event, newValue) => handleFreshBatchChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Färsk batch"
                    helperText="Valfritt. Koppla råvaran till en batch för spårbarhet."
                    size="small"
                    fullWidth
                    disabled={formLocked}
                    placeholder={!selectedProduct ? "Välj först en produkt" : getFreshBatchesForProduct(selectedProduct.id).length === 0 ? "Inga batcher för produkten — lägg till under Färska batcher" : "Välj batch"}
                  />
                )}
                disabled={formLocked || !selectedProduct}
                fullWidth
              />
            </div>
              </>
            ) : (
              <div className="FreezeDryExternalFields">
                <p className="AddPlantDialogBottomItemText">EXTERNT INKÖPT RÅVARA</p>
                <div className="FreezeDryExternalFieldGrid">
                  <TextField
                    label="Produktnamn *"
                    size="small"
                    value={externalProductName}
                    onChange={(e) => setExternalProductName(e.target.value)}
                    disabled={formLocked}
                    fullWidth
                  />
                  <TextField
                    label="Ursprungsland"
                    size="small"
                    value={externalCountry}
                    onChange={(e) => setExternalCountry(e.target.value)}
                    disabled={formLocked}
                    fullWidth
                  />
                  <TextField
                    label="Inköpt från"
                    size="small"
                    value={externalPurchasedFrom}
                    onChange={(e) => setExternalPurchasedFrom(e.target.value)}
                    disabled={formLocked}
                    fullWidth
                  />
                  <TextField
                    label="Pris per kg (kr)"
                    type="number"
                    size="small"
                    value={externalPricePerKg ?? ""}
                    onChange={(e) => setExternalPricePerKg(e.target.value ? Number(e.target.value) : null)}
                    disabled={formLocked}
                    fullWidth
                  />
                </div>
              </div>
            )}

            </fieldset>
            <fieldset className="BatchFormSection">
              <legend>Tid och ansvarig</legend>
            {/* Process start date & time */}
            <div
              style={{
                width: "100%",
                marginBottom: 16,
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Startdatum *"
                  inputFormat="YYYY-MM-DD"
                  value={freezeDryStart}
                  onChange={(newValue) => {
                    // @ts-ignore
                    setFreezeDryStart(newValue?.valueOf() || null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      {...params}
                      disabled={formLocked}
                    />
                  )}
                  disabled={formLocked}
                />
                <TimePicker
                  label="Starttid"
                  ampm={false}
                  value={freezeDryStartTime}
                  onChange={(newValue) => setFreezeDryStartTime(newValue)}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      {...params}
                      disabled={formLocked}
                    />
                  )}
                  disabled={formLocked}
                />
              </LocalizationProvider>
            </div>

            {/* Process end date & time */}
            <div
              style={{
                width: "100%",
                marginBottom: 16,
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Slutdatum"
                  inputFormat="YYYY-MM-DD"
                  value={freezeDryEnd}
                  onChange={(newValue) => {
                    // @ts-ignore
                    setFreezeDryEnd(newValue?.valueOf() || null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      {...params}
                      disabled={formLocked}
                    />
                  )}
                  disabled={formLocked}
                />
                <TimePicker
                  label="Sluttid"
                  ampm={false}
                  value={freezeDryEndTime}
                  onChange={(newValue) => setFreezeDryEndTime(newValue)}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      {...params}
                      disabled={formLocked}
                    />
                  )}
                  disabled={formLocked}
                />
              </LocalizationProvider>
            </div>

            <div style={{ width: "100%", marginBottom: 16 }}>
              <Autocomplete
                freeSolo
                options={PLANTED_BY_OPTIONS}
                inputValue={freezeDryBy}
                onInputChange={(_event, value) => setFreezeDryBy(value)}
                disabled={formLocked}
                renderInput={(params) => <TextField {...params} label="Processad av" size="small" helperText="Välj en person eller skriv ett namn." />}
              />
            </div>
            </fieldset>
            <fieldset className="BatchFormSection">
              <legend>Mängd och anteckningar</legend>
            {/* Tray count is only relevant for internally grown batches. */}
            {sourceType === "grown_by_us" && (
              <div style={{ width: "100%", marginBottom: 16 }}>
                <TextField
                  label="Antal brickor"
                  type="number"
                  size="small"
                  value={trayCount ?? ""}
                  onChange={(e) =>
                    setTrayCount(e.target.value ? Number(e.target.value) : null)
                  }
                  disabled={formLocked}
                  fullWidth
                />
              </div>
            )}

            {/* Fresh weight & Dry weight */}
            <div
              style={{
                width: "100%",
                marginBottom: 16,
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <TextField
                label="Färskvikt (g)"
                type="number"
                size="small"
                value={freshWeight ?? ""}
                onChange={(e) =>
                  setFreshWeight(
                    e.target.value ? Number(e.target.value) : null
                  )
                }
                disabled={formLocked}
              />
              <TextField
                label="Torrvikt (g)"
                type="number"
                size="small"
                value={dryWeight ?? ""}
                onChange={(e) =>
                  setDryWeight(
                    e.target.value ? Number(e.target.value) : null
                  )
                }
                disabled={formLocked}
              />
            </div>

            {/* Extra info */}
            <div style={{ width: "100%", marginBottom: 16 }}>
              <TextField
                label="Anteckningar"
                multiline
                rows={3}
                size="small"
                fullWidth
                value={extraInfo}
                onChange={(e) => setExtraInfo(e.target.value)}
                disabled={formLocked}
              />
            </div>
            </fieldset>
          </div>
        </DialogContent>
        {saveError && <Alert severity="error" className="BatchSaveError">{saveError}</Alert>}
        <DialogActions>
          <Button
            onClick={handleClose}
            color="inherit"
            disabled={formLocked}
          >
            Avbryt
          </Button>
          <Button
            onClick={handleSave}
            color="inherit"
            variant="contained"
            disabled={
              dialogLoading || writer.loading || writer.saving || writer.conflict ||
              (protectedWriter && (!writer.reason.trim() || (!!editingBatch && !writer.version))) ||
              (!writer.pending && ((sourceType === "grown_by_us" && !selectedProduct) || (sourceType === "external" && !externalProductName.trim())))
            }
          >
            {dialogLoading || writer.saving ? "Sparar…" : writer.pending ? "Försök igen" : editingBatch ? "Spara ändringar" : "Skapa batch"}
          </Button>
        </DialogActions>
      </Dialog>
      {deleteTarget && <WebshopCatalogDeletion kind="processing-batch" documentId={deleteTarget.firebaseId}
        name={`${deleteTarget.batchId} (${deleteTarget.product})`} user={user} onClose={() => setDeleteTarget(null)}
        onDeleted={() => setBatchesByProcess((prev) => ({
          freeze_dried: prev.freeze_dried.filter((batch) => batch.firebaseId !== deleteTarget.firebaseId),
          dehydrated: prev.dehydrated.filter((batch) => batch.firebaseId !== deleteTarget.firebaseId),
        }))} />}
    </div>
  );
}

export default DashBoardFreezeDry;
